import { Component, computed, effect, inject, signal } from "@angular/core"
import { httpResource } from "@angular/common/http"
import { Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { catchError, of } from "rxjs"
import { ToastService } from "src/app/services"
import { Category, CategoryTypes, Column, PagedAward, TableLazyLoadEvent } from "../../models"
import { AwardsService, CategoryService } from "../../services"
import { ColumnFilter, toPagedParams } from "../../helpers/TablePaging"
import { TableComponent } from "../../components/table/table.component"

@Component({
    selector: "valben-awards-table",
    templateUrl: "./awards-table.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [TableComponent],
})
export class AwardsTablePage {
    private _awardsService = inject(AwardsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    cols: Column[] = [
        { field: "names", subField: "hr", header: "Naslov", bodyType: "langs", filterType: "text", filterMode: "contains", pairWith: "descriptions" },
        { field: "descriptions", subField: "hr", header: "Opis", bodyType: "langs", filterType: "text", filterMode: "contains" },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "isActive", header: "Aktivan", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Aktivna", falseText: "Neaktivna", pairWith: "isDraft" },
        { field: "isDraft", header: "Skica", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Skica", falseText: "Bez skice" },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true, prefix: "v" },
        { field: "imageUrl", header: "Slika", bodyType: "file", icon: "image" },
        { field: "categoryIds", header: "Kategorije", bodyType: "modal", filterType: "cats", filterMode: "inArr", icon: "sell" },
    ]

    private _pageRequest = signal<TableLazyLoadEvent | undefined>(undefined)

    private _allCategories = toSignal(
        inject(CategoryService)
            .GetAllCached()
            .pipe(catchError(() => of([] as Category[])))
    )

    private _categories = computed(() => (this._allCategories() ?? []).filter(c => c.type === CategoryTypes.Award))

    private _categoryNames = computed(() => new Map(this._categories().map(c => [c.masterId, c.names?.["hr"] ?? ""])))

    awards = httpResource<PagedAward>(() => {
        const event = this._pageRequest()
        const categories = this._allCategories()
        if (!event || categories === undefined) return undefined

        const resolveCats = (names: string[]): ColumnFilter | null => {
            const ids = this._categories()
                .filter(c => names.includes(c.names?.["hr"]))
                .map(c => c.masterId)
            return ids.length ? { field: "categoryIds", value: ids.join(","), matchMode: "in" } : null
        }

        return this._awardsService.PagedRequest(toPagedParams(event, this.cols, resolveCats))
    })

    tableValues = computed(() => {
        const names = this._categoryNames()
        return (this.awards.value()?.items ?? []).map(award => ({
            ...award,
            createdDate: new Date(award.createdDate),
            modifiedDate: new Date(award.modifiedDate),
            categoryIds: award.categoryNames ?? (award.categoryIds ?? []).map(id => names.get(id) ?? id),
        }))
    })

    totalRecords = computed(() => this.awards.value()?.totalCount ?? 0)
    loading = computed(() => this.awards.isLoading())

    refresh = () => this.awards.reload()

    constructor() {
        effect(() => {
            if (!this.awards.error()) return
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Došlo je do pogreške prilikom dohvata nagrada.",
                life: 10000,
            })
        })
    }

    loadPage(event: TableLazyLoadEvent): void {
        setTimeout(() => this._pageRequest.set(event))
    }

    add(): void {
        this._router.navigate(["/awards/add"])
    }

    edit(masterId: string): void {
        this._router.navigate(["/awards/edit", masterId])
    }
}
